import { useState, useRef, useEffect } from "react";
import { Check } from "lucide-react";
import { useNavigate, useLocation, useParams } from "react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { accountsAtom, selectedAccountAtom, setSelectedAccountAtom } from "../stores/galaxy";
import { affiliationsAtom, affiliationsLoadedAtom, refreshAffiliationsAtom } from "../stores/affiliations";
import { sharedGalaxiesAtom, sharedGalaxiesLoadedAtom, refreshSharedGalaxiesAtom } from "../stores/shared";
import { setSelectedDomainAtom } from "../stores/observatory";
import { workerAtom } from "../stores/worker";
import { localeAtom } from "../stores/locale";
import { t } from "../lib/i18n";
import { useAccountPlans } from "../hooks/useAccountPlans";
import { PlanBadge } from "./PlanBadge";
import { type Affiliation } from "../api/client";

// archiveTenure renders a departed galaxy's "joined–left" year range, the quiet
// marker that an archive row is a record, not a live galaxy you can switch into.
function archiveTenure(a: Affiliation): string {
  const year = (s: string) => (s ? new Date(s).getFullYear() : NaN);
  const from = year(a.joined_at);
  const to = year(a.departed_at);
  if (!Number.isNaN(from) && !Number.isNaN(to)) return `${from}–${to}`;
  return Number.isNaN(to) ? "" : `${to}`;
}

export default function GalaxySwitcher() {
  const accounts = useAtomValue(accountsAtom);
  const plans = useAccountPlans(accounts);
  const { account: urlAccount, galaxyId } = useParams<{ account: string; galaxyId: string }>();
  // On a /:account/affiliations/:galaxyId page the switcher's "current" is the
  // ARCHIVED galaxy (a record), not the active one in the URL. We surface it the
  // same way an archive row reads — name + tenure range, no plan badge — so the
  // switcher looks and behaves identically whether you're operating or reviewing.
  const archiveId = Number(galaxyId) || 0;
  const onArchive = archiveId > 0;
  // URL is the source of truth. The atom is a fallback for when the
  // switcher renders outside a galaxy-scoped route (none today, but
  // keeps the chrome resilient if we add a galaxy-less surface).
  const atomAccount = useAtomValue(selectedAccountAtom);
  const selectedAccount = urlAccount || atomAccount;
  const setSelectedAccount = useSetAtom(setSelectedAccountAtom);
  const setSelectedDomain = useSetAtom(setSelectedDomainAtom);
  const setWorker = useSetAtom(workerAtom);
  const locale = useAtomValue(localeAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Archive: the galaxies the user has LEFT (departed memberships). Held in a
  // SHARED atom (not local state) so a departure performed elsewhere — the
  // member_removed webhook or the observation-driven residency reconcile — can
  // refresh it without a page reload. Fetched lazily on first need; an empty/failed
  // fetch simply hides the archive section. See stores/affiliations.
  const archive = useAtomValue(affiliationsAtom);
  const archiveLoaded = useAtomValue(affiliationsLoadedAtom);
  const refreshArchive = useSetAtom(refreshAffiliationsAtom);
  // Shared: read-only galaxies an owner granted the viewer (docs/design/
  // shared-galaxies.md). Same lazy-atom pattern as archive — loaded on first open,
  // refreshed out-of-band by claim/leave so the section never needs a reload.
  const shared = useAtomValue(sharedGalaxiesAtom);
  const sharedLoaded = useAtomValue(sharedGalaxiesLoadedAtom);
  const refreshShared = useSetAtom(refreshSharedGalaxiesAtom);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load eagerly when we're ON an archive page (the button needs the current
    // archive's name + tenure), otherwise lazily on first open. The refresh
    // action dedups in-flight requests and is idempotent once loaded.
    if ((!open && !onArchive) || archiveLoaded) return;
    void refreshArchive();
  }, [open, onArchive, archiveLoaded, refreshArchive]);

  useEffect(() => {
    // Lazily load the shared-galaxies list on first open (idempotent once loaded).
    if (!open || sharedLoaded) return;
    void refreshShared();
  }, [open, sharedLoaded, refreshShared]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Hide only when there's genuinely nothing to show. On an archive page we keep
  // rendering even before the active list resolves, so the record still has a
  // switcher (and a route back to a live galaxy).
  if (accounts.length === 0 && !onArchive && shared.length === 0) return null;

  const currentArchive = onArchive ? archive.find((a) => a.galaxy_id === archiveId) : undefined;
  const buttonLogin = (onArchive ? currentArchive?.login : selectedAccount) || selectedAccount;

  const handleSelect = (account: string) => {
    setSelectedAccount(account);
    setSelectedDomain("");
    setWorker({ active: false, phase: "idle", detail: "", step: 0, total: 0, startedAt: null, org: "", error: "" });
    setOpen(false);

    // From a user-scoped page with no :account in the URL (the archive
    // /me/affiliations/... or /me/career), switching to an active galaxy returns
    // to its operational root: those sub-paths are user-scoped, not portable
    // across galaxies, and urlAccount is absent so the page-preserving copy below
    // can't run anyway. (Genuinely galaxy-scoped pages keep their sub-path under
    // the new galaxy via that copy.)
    if (onArchive || !urlAccount) {
      navigate(`/${account}`);
      return;
    }
    // Navigate to same page under new galaxy
    const match = location.pathname.match(/^\/[^/]+(\/.*)?$/);
    const subPath = match?.[1] || "";
    navigate(`/${account}${subPath}`);
  };

  // Opening an archive row does NOT switch galaxy scope (a departed galaxy is
  // outside your universe — you can't operate in it). It navigates to your own
  // user-scoped record for that galaxy under /me (no :account at all). This
  // asymmetry IS the galaxy-boundary safety: X-Account-Login never points at a
  // galaxy you've left, and the record is keyed off the JWT, not an account.
  const openArchive = (galaxyId: number) => {
    setOpen(false);
    navigate(`/me/affiliations/${galaxyId}`);
  };

  return (
    <div ref={ref} className="relative px-md pb-md">
      {/* Selected galaxy button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative flex w-full items-center gap-md overflow-hidden rounded-xl px-md py-md text-sm font-medium text-gruvbox-fg transition-all hover:brightness-110"
        style={{
          background: "linear-gradient(135deg, rgba(29,32,48,0.95) 0%, rgba(13,17,23,0.98) 50%, rgba(24,20,40,0.95) 100%)",
          border: "1px solid rgba(250,189,47,0.15)",
          boxShadow: "inset 0 1px 0 rgba(250,189,47,0.05), 0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        {/* Galaxy shimmer effect */}
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background: "radial-gradient(ellipse at 20% 50%, rgba(250,189,47,0.15) 0%, transparent 50%), radial-gradient(ellipse at 80% 30%, rgba(131,165,152,0.1) 0%, transparent 40%)",
          }}
        />
        <img
          src={`https://github.com/${buttonLogin}.png?size=32`}
          alt=""
          className="relative h-6 w-6 rounded-full ring-1 ring-gruvbox-yellow/20"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
        <span className="relative flex-1 truncate text-left">{buttonLogin || "Select Galaxy"}</span>
        {onArchive
          ? currentArchive && (
              <span className="relative text-[10px] tabular-nums text-gruvbox-gray/70">{archiveTenure(currentArchive)}</span>
            )
          : selectedAccount && <span className="relative"><PlanBadge plan={plans[selectedAccount]} /></span>}
        <svg
          className={`relative h-4 w-4 text-gruvbox-yellow/50 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute left-3 right-3 z-50 mt-sm overflow-hidden rounded-xl shadow-2xl"
          style={{
            background: "linear-gradient(180deg, rgba(29,32,48,0.98) 0%, rgba(13,17,23,0.99) 100%)",
            border: "1px solid rgba(250,189,47,0.12)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(250,189,47,0.05)",
          }}
        >
          {/* Section header only appears once there's another section (shared or
              archive) to separate FROM — the common single-section case keeps the
              plain list it had. */}
          {(archive.length > 0 || shared.length > 0) && (
            <div className="px-md pt-md pb-xs text-[10px] font-semibold uppercase tracking-wider text-gruvbox-gray/70">
              {t("galaxySwitcher.active", locale)}
            </div>
          )}
          {accounts.map((account) => {
            const isSel = !onArchive && selectedAccount === account;
            return (
            <button
              key={account}
              onClick={() => handleSelect(account)}
              className={`flex w-full items-center gap-md px-md py-md text-sm transition-all ${
                isSel
                  ? "text-gruvbox-yellow"
                  : "text-gruvbox-fg1 hover:text-gruvbox-fg"
              }`}
              style={isSel ? {
                background: "linear-gradient(90deg, rgba(250,189,47,0.1) 0%, rgba(250,189,47,0.03) 100%)",
              } : {}}
            >
              {/* Fixed-width check slot to the LEFT of the avatar — present on
                  every row (empty when unselected) so the avatar / name / badge
                  columns line up across rows and the badge never shifts. Kept
                  narrow and pulled tight to the avatar (-mr) so the empty slot
                  doesn't read as dead space on unselected rows. */}
              <span className="-mr-sm flex w-3 shrink-0 items-center justify-center text-gruvbox-yellow">
                {isSel && <Check size={12} />}
              </span>
              <img
                src={`https://github.com/${account}.png?size=32`}
                alt=""
                className={`h-5 w-5 rounded-full ring-1 ${
                  isSel ? "ring-gruvbox-yellow/30" : "ring-white/10"
                }`}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <span className="truncate">{account}</span>
              <span className="ml-auto"><PlanBadge plan={plans[account]} /></span>
            </button>
            );
          })}

          {/* Shared: read-only galaxies an owner granted you. A live switch target
              (navigates to /{login} like an active galaxy) — the backend serves
              the read allowlist and clamps every member to Member-N for a guest.
              A "read-only" tag stands in for the plan badge you don't hold here. */}
          {shared.length > 0 && (
            <>
              <div className="px-md pt-md pb-xs text-[10px] font-semibold uppercase tracking-wider text-gruvbox-gray/70">
                {t("galaxySwitcher.shared", locale)}
              </div>
              {shared.map((g) => {
                const isSel = !onArchive && selectedAccount === g.account_login;
                return (
                  <button
                    key={g.account_id}
                    onClick={() => handleSelect(g.account_login)}
                    className={`flex w-full items-center gap-md px-md py-md text-sm transition-all ${
                      isSel ? "text-gruvbox-yellow" : "text-gruvbox-fg1 hover:text-gruvbox-fg"
                    }`}
                    style={isSel ? { background: "linear-gradient(90deg, rgba(250,189,47,0.1) 0%, rgba(250,189,47,0.03) 100%)" } : {}}
                  >
                    <span className="-mr-sm flex w-3 shrink-0 items-center justify-center text-gruvbox-yellow">
                      {isSel && <Check size={12} />}
                    </span>
                    <img
                      src={`https://github.com/${g.account_login}.png?size=32`}
                      alt=""
                      className={`h-5 w-5 rounded-full ring-1 ${isSel ? "ring-gruvbox-yellow/30" : "ring-white/10"}`}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                    <span className="truncate">{g.account_login}</span>
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-gruvbox-gray/60">
                      {t("sharedGalaxies.viewer.readOnly", locale)}
                    </span>
                  </button>
                );
              })}
            </>
          )}

          {/* Archive: galaxies you've LEFT. Dimmed, no plan badge (you're not
              billed there), a tenure range instead — a record, not a switch
              target. Clicking opens your own record, never the live galaxy. */}
          {archive.length > 0 && (
            <>
              <div className="px-md pt-md pb-xs text-[10px] font-semibold uppercase tracking-wider text-gruvbox-gray/70">
                {t("galaxySwitcher.archive", locale)}
              </div>
              {archive.map((a) => {
                const cur = onArchive && a.galaxy_id === archiveId;
                return (
                <button
                  key={a.galaxy_id}
                  onClick={() => openArchive(a.galaxy_id)}
                  className={`flex w-full items-center gap-md px-md py-md text-sm transition-all ${
                    cur ? "text-gruvbox-yellow" : "text-gruvbox-fg1/50 hover:text-gruvbox-fg1/80"
                  }`}
                  style={cur ? { background: "linear-gradient(90deg, rgba(250,189,47,0.1) 0%, rgba(250,189,47,0.03) 100%)" } : {}}
                >
                  <span className="-mr-sm flex w-3 shrink-0 items-center justify-center text-gruvbox-yellow">
                    {cur && <Check size={12} />}
                  </span>
                  <img
                    src={`https://github.com/${a.login}.png?size=32`}
                    alt=""
                    className={`h-5 w-5 rounded-full ring-1 ${cur ? "ring-gruvbox-yellow/30" : "opacity-60 ring-white/5"}`}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <span className="truncate">{a.login}</span>
                  <span className="ml-auto text-[10px] tabular-nums text-gruvbox-gray/60">
                    {archiveTenure(a)}
                  </span>
                </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
